<script setup lang="ts">
  import { computed, reactive, ref, watch, watchEffect } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { Trash2 } from 'lucide-vue-next'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import { AnalysisConfigSchema } from '../models/AnalysisConfigSchema'
  import { AnalysisConfigField } from '../models/AnalysisConfigField'
  import { AnalysisConfigFieldType } from '../models/AnalysisConfigFieldType'
  import { AnalysisConfig } from '../models/AnalysisConfig'
  import { AnalysisConfigService } from '../services/AnalysisConfigService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import AnalysisConfigConfiguration from './AnalysisConfigConfiguration.vue'

  interface IArrayAnalysisConfigBuilderProps {
    configSchemas: Array<AnalysisConfigSchema>
    availableColumns: Array<{ value: string; label: string }>
    analysisConfigField: AnalysisConfigField
    rootAnalysisConfig: AnalysisConfig | null
    modelValue: any[]
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface IArrayAnalysisConfigBuilderEmits {
    (e: 'update:modelValue', value: Array<any>): void
    (e: 'child-config-type-changed', type: string, config: AnalysisConfig, configuration: {}): void
  }

  const props = defineProps<IArrayAnalysisConfigBuilderProps>()
  const emit = defineEmits<IArrayAnalysisConfigBuilderEmits>()

  const arrayAnalysisConfigField = reactive<AnalysisConfigField>(props.analysisConfigField)

  if (arrayAnalysisConfigField.field_type !== AnalysisConfigFieldType.Array)
    throw new Error('ArrayAnalysisConfigBuilder requires an Array type AnalysisConfigField!')
  if (arrayAnalysisConfigField.item_schema == null)
    throw new Error('Array type AnalysisConfigFields require an item_schema!')

  // collection of configs that make up sub-items
  const analysisConfigs = ref<Array<AnalysisConfig>>([])

  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // Drag / reorder state
  const draggedIndex = ref<number | null>(null)
  const dragOverIndex = ref<number | null>(null)
  const instanceId = `arrayAnalysisConfig-${Math.random().toString(36).slice(2)}`

  const moveItem = (from: number, to: number) => {
    if (from === to) return
    const item = analysisConfigs.value.splice(from, 1)[0]
    analysisConfigs.value.splice(to, 0, item)
  }

  const moveUp = (index: number) => {
    if (index <= 0) return
    moveItem(index, index - 1)
  }

  const moveDown = (index: number) => {
    if (index >= analysisConfigs.value.length - 1) return
    moveItem(index, index + 1)
  }

  const handleDragLeave = () => {
    dragOverIndex.value = null
  }

  const handleDragStart = (event: DragEvent, index: number) => {
    event.stopPropagation()
    draggedIndex.value = index
    const id = instanceId
    // store origin in dataTransfer and window fallback
    try {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
        event.dataTransfer.setData('application/x-array-builder-id', id)
      }
    } catch {}
    ;(window as any).__arrayBuilderDragSourceId = id
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) {
      dragOverIndex.value = -1
      return
    }
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    dragOverIndex.value = index
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) return

    if (draggedIndex.value == null) return    
    const from = draggedIndex.value
    const to = targetIndex
    if (from === to) {
      draggedIndex.value = null
      dragOverIndex.value = -1
      ;(window as any).__arrayBuilderDragSourceId = null
      return
    }
    moveItem(from, to)
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleDragEnd = () => {
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const configsAreEqual = (incoming: any, internal: Array<AnalysisConfig>) => {
    if (!Array.isArray(incoming) || !Array.isArray(internal)) return false
    if (incoming.length !== internal.length) return false
    for (let i = 0; i < incoming.length; i++) {
      const inc = incoming[i]?.configuration ?? incoming[i]
      const cur = internal[i]?.configuration ?? internal[i]
      try {
        if (JSON.stringify(inc) !== JSON.stringify(cur)) return false
      } catch {
        return false
      }
    }
    return true
  }

  watch(
    () => props.modelValue,
    (newVal) => {
      if (configsAreEqual(newVal, analysisConfigs.value)) return
      analysisConfigs.value = []
      if (Array.isArray(newVal)) {
        newVal.forEach((value: any) => {
          const cfg = AnalysisConfigService.createAnalysisConfigFromField(
            arrayAnalysisConfigField.item_schema!
          )
          // if incoming value already contains configuration, use it; otherwise use value directly
          cfg.configuration =
            value?.configuration != null
              ? (AnalysisConfigService.getFlattenedAnalysisConfig(value, null) as any)
              : value
          // ensure configuration records the type for editor / downstream logic
          cfg.configuration._type = cfg._type
          analysisConfigs.value.push(cfg)
        })
      }
    },
    { immediate: true, deep: true }
  )

  watch(
    analysisConfigs,
    (newValue) => {
      emit('update:modelValue', newValue)
    },
    { deep: true }
  )

  // Initialize configuration with default values for object item_schema
  const initializeConfiguration = (cfg: AnalysisConfig, fields: Array<AnalysisConfigField>) => {
    if (cfg == null) throw new Error('config is required')
    if (arrayAnalysisConfigField.item_schema?.field_type !== AnalysisConfigFieldType.Object) return
    const config = AnalysisConfigService.initializeAnalysisConfigFieldConfiguration(fields)
    cfg.configuration = config
  }

  const isAddDisabled = computed(() => {
    const maxItems = (arrayAnalysisConfigField.field_type_args as any)?.array_max_items
    if (maxItems === undefined || maxItems === null) return false
    if (typeof maxItems !== 'number' || isNaN(maxItems) || maxItems < 0) return false
    return analysisConfigs.value.length >= maxItems
  })

  const addAnalysisConfig = () => {
    const newCfg = AnalysisConfigService.createAnalysisConfigFromField(
      arrayAnalysisConfigField.item_schema!
    )
    initializeConfiguration(newCfg, arrayAnalysisConfigField.item_schema?.fields || [])
    analysisConfigs.value.push(newCfg)
    message.success(
      `Added analysis configuration: ${arrayAnalysisConfigField.display_name}`,
      defaultMessageDurationSeconds
    )
    return newCfg
  }

  const removeAnalysisConfig = (index: number) => {
    analysisConfigs.value.splice(index, 1)
  }

  const handleConfigurationUpdate = (
    cfg: AnalysisConfig,
    config: Record<string, any>,
    configTypeChange?: boolean
  ) => {
    const cfgIndex = analysisConfigs.value.findIndex((r) => r.id === cfg.id)
    if (cfgIndex > -1) {
      analysisConfigs.value[cfgIndex].configuration = { ...config }
    }
    if (configTypeChange === true) {
      emit('child-config-type-changed', cfg._type, cfg, { ...config })
    }
  }
</script>

<template>
  <div
    v-if="arrayAnalysisConfigField.item_schema?.field_type === AnalysisConfigFieldType.Object"
    class="analysisconfig-array-object-builder-container"
  >
    <div class="mt-[-40px] self-end flex items-center gap-2">
      <a-tooltip placement="top">
        <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
        <Button
          style="padding: 3px; border: none"
          type="text"
          size="large"
          class="flex items-center justify-center"
          @click="toggleGlobalCollapse"
          :disabled="analysisConfigs.length === 0"
          :aria-disabled="analysisConfigs.length === 0"
          aria-label="Toggle Collapse All"
        >
          <CompressOutlined v-if="isCollapsed" class="text-base" style="line-height: 1; margin: 0" />
          <ExpandOutlined v-else class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </a-tooltip>

      <Button
        type="primary"
        shape="circle"
        size="large"
        class="flex items-center justify-center"
        @click="addAnalysisConfig"
        :title="`Add ${arrayAnalysisConfigField.item_schema!.display_name}`"
        :disabled="isAddDisabled"
        :aria-disabled="isAddDisabled"
      >
        <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
      </Button>
    </div>

    <div class="mt-5">
      <div
        class="analysisconfig-mapping-row"
        v-for="(cfg, index) in analysisConfigs"
        :key="cfg.id"
        @dragover="handleDragOver($event, index)"
        @dragleave="handleDragLeave"
        @drop="handleDrop($event, index)"
        :class="{ 'drop-zone-active': dragOverIndex === index }"
      >
        <div
          class="drag-handle"
          draggable="true"
          @dragstart="handleDragStart($event, index)"
          @dragend="handleDragEnd"
          tabindex="0"
          role="button"
          :aria-grabbed="draggedIndex === index"
          aria-label="Drag to reorder"
        >
          <HolderOutlined class="text-base" style="line-height: 1; margin: 0" />
        </div>

        <AnalysisConfigConfiguration
          :configuration-level="props.configurationLevel + 1"
          :root-analysis-config="props.rootAnalysisConfig"
          :analysisConfig="cfg"
          :availableColumns="availableColumns"
          @configuration-updated="handleConfigurationUpdate"
          :configSchemas="configSchemas"
          :isCondensedLayout="props.isCondensedLayout"
          :globalCollapsed="globalCollapsed"
          :current-path="
            props.currentPath
              ? [...props.currentPath, String(index)]
              : [arrayAnalysisConfigField.name, String(index)]
          "
        />

        <div class="action-buttons ml-2 flex flex-col items-center">
          <a-tooltip placement="left">
            <template #title>Move Up</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveUp(index)"
              :disabled="index === 0"
              :aria-disabled="index === 0"
            >
              <ArrowUpOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>
              {{ `Remove ${arrayAnalysisConfigField.item_schema!.display_name}` }}
            </template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn ant-btn-dangerous my-1"
              @click="removeAnalysisConfig(index)"
            >
              <Trash2 class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>Move Down</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveDown(index)"
              :disabled="index === analysisConfigs.length - 1"
              :aria-disabled="index === analysisConfigs.length - 1"
            >
              <ArrowDownOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .analysisconfig-array-object-builder-container {
    display: flex;
    flex-direction: column;
  }

  .analysisconfig-mapping-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 15px;
  }

  .drag-handle {
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground, #6b7280);
  }

  .compact-action-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .compact-action-btn.compact-action-btn.ant-btn-dangerous {
    padding: 0;
  }

  .compact-icon {
    display: inline-flex;
  }

  /* Ensure SVG-based icons (lucide) respect compact sizing */
  .compact-icon,
  .compact-icon svg {
    width: 14px !important;
    height: 14px !important;
    line-height: 1 !important;
  }

  .analysisconfig-mapping-row.drop-zone-active {
    outline: 2px dashed rgba(24, 144, 255, 0.5);
    background: rgba(24, 144, 255, 0.03);
  }

  .action-buttons .ant-btn {
    padding: 3px;
  }

  select {
    padding: 4px;
  }
</style>
